// ============================================================================
// @torre/expenses — Conciliación del estado de cuenta (#954, épica #959)
// ============================================================================
// Que cada estado de cuenta cuadre contra el total que declara el banco y que
// el mismo dinero no se cuente dos veces:
//
//   · cada movimiento lleva un TIPO: gasto, pago a tarjeta propia, traspaso
//     entre cuentas propias, devolución o ingreso. Solo «gasto» (y la
//     devolución, que resta) llega a los totales;
//   · un pago desde cheques a una tarjeta propia registrada es traspaso: los
//     cargos ya se cuentan en el estado de cuenta de la tarjeta;
//   · una devolución se liga al gasto que revierte;
//   · estado por importación: por_conciliar / conciliado / con_diferencia.
//
// Sin I/O. Nada importado de `./index` en el nivel superior (ver accounts.ts).
// ============================================================================

import { BASE_CURRENCY, daysBetween, normalizeText, round2, vendorSimilarity, type Expense, type ExtractedCharge } from "./index";
import { accountLabel, type ExpenseAccount } from "./accounts";

export const MOVEMENT_KINDS = ["gasto", "pago_tarjeta_propia", "traspaso_propio", "devolucion", "ingreso"] as const;
export type MovementKind = (typeof MOVEMENT_KINDS)[number];

export const MOVEMENT_KIND_LABELS: Record<MovementKind, { es: string; en: string }> = {
  gasto: { es: "Gasto", en: "Expense" },
  pago_tarjeta_propia: { es: "Pago a tarjeta propia", en: "Payment to own card" },
  traspaso_propio: { es: "Traspaso entre cuentas propias", en: "Transfer between own accounts" },
  devolucion: { es: "Abono / devolución", en: "Credit / refund" },
  ingreso: { es: "Ingreso", en: "Income" },
};

export const RECONCILE_STATUSES = ["por_conciliar", "conciliado", "con_diferencia"] as const;
export type ReconcileStatus = (typeof RECONCILE_STATUSES)[number];

/** Cargo de una importación con su tipo y, si es devolución, a qué revierte. */
export type StatementCharge = ExtractedCharge & {
  index: number;
  kind?: MovementKind;
  /** Por qué no es gasto (o por qué se clasificó así). */
  kindReason?: string | null;
  /** Cuenta propia a la que va el pago o traspaso. */
  relatedAccountId?: string | null;
  /** Devolución: gasto existente que revierte… */
  revertsExpenseId?: string | null;
  /** …o cargo de la misma importación que revierte. */
  revertsIndex?: number | null;
};

const PAY_CARD = /\b(pago (a |de |tdc|tc|tarjeta|recibido|gracias|bmovil|spei tarjeta)|pago tarjeta|su pago|gracias por su pago|payment (thank you|received)|pago tdc|pago tc|abono a tarjeta)\b/;
const OWN_TRANSFER = /\b(traspaso|transferencia entre cuentas|entre cuentas propias|cuenta propia|a cuenta propia|mismo titular)\b/;
const REFUND = /\b(devolucion|reembolso|refund|bonificacion|reverso|reversa|cancelacion|contracargo|ajuste a favor|credit adjustment)\b/;
const REFUND_WORDS = /\b(devolucion|reembolso|refund|bonificacion|reverso|reversa|cancelacion|contracargo|ajuste|a favor|credit|adjustment|abono|dev)\b/g;

/** Últimos 4 dígitos de cada número largo que aparece en el texto. */
function mentionedLast4(text: string): string[] {
  const runs = String(text || "").match(/[\d*xX]{4,}/g) || [];
  return runs.map((r) => r.replace(/\D/g, "")).filter((d) => d.length >= 4).map((d) => d.slice(-4));
}

export interface ClassifyContext {
  /** Cuenta del estado de cuenta (si ya se sabe). */
  account?: ExpenseAccount | null;
  /** Todas las cuentas de la empresa. */
  accounts?: readonly ExpenseAccount[];
}

export interface Classification {
  kind: MovementKind;
  reason: string | null;
  relatedAccountId: string | null;
}

/**
 * Tipo de un movimiento del estado de cuenta. Reglas (en orden):
 *  1. Cargo que menciona una TARJETA DE CRÉDITO propia registrada (sus últimos
 *     4) → pago a tarjeta propia.
 *  2. Cargo que menciona otra cuenta propia, o dice «traspaso»/«cuenta propia»
 *     → traspaso entre cuentas propias.
 *  3. En el estado de una tarjeta, un abono que es pago (o que menciona una
 *     cuenta propia) → pago a tarjeta propia (el otro lado del punto 1).
 *  4. Abono que menciona otra cuenta propia o dice «traspaso» → traspaso.
 *  5. Abono con «devolución», «reembolso», «bonificación»… → devolución. En una
 *     tarjeta, cualquier otro abono también es devolución; en cheques es ingreso.
 *  6. Lo demás es gasto.
 */
export function classifyMovement(
  charge: Pick<ExtractedCharge, "vendor" | "description" | "isCredit">,
  ctx: ClassifyContext = {},
): Classification {
  const text = normalizeText(`${charge.vendor || ""} ${charge.description || ""}`);
  const raw = `${charge.vendor || ""} ${charge.description || ""}`;
  const own = (ctx.accounts || []).filter((a) => a.last4 && a.id !== ctx.account?.id);
  const digits = mentionedLast4(raw);
  const mentioned = own.filter((a) => digits.includes(a.last4 as string));
  const card = mentioned.find((a) => a.type === "credito");
  const other = mentioned.find((a) => a.type !== "credito") || mentioned[0];
  const isCardStatement = ctx.account?.type === "credito";

  if (!charge.isCredit) {
    if (card) return { kind: "pago_tarjeta_propia", reason: `Pago a la tarjeta propia ${accountLabel(card)}: sus cargos ya se cuentan en su estado de cuenta`, relatedAccountId: card.id };
    if (other) return { kind: "traspaso_propio", reason: `Traspaso a la cuenta propia ${accountLabel(other)}`, relatedAccountId: other.id };
    if (OWN_TRANSFER.test(text)) return { kind: "traspaso_propio", reason: "El banco lo describe como traspaso entre cuentas propias", relatedAccountId: null };
    return { kind: "gasto", reason: null, relatedAccountId: null };
  }
  if (isCardStatement && (PAY_CARD.test(text) || mentioned.length)) {
    const from = other || null;
    return { kind: "pago_tarjeta_propia", reason: from ? `Pago recibido desde ${accountLabel(from)}` : "Pago recibido en la tarjeta: no es gasto ni ingreso", relatedAccountId: from ? from.id : null };
  }
  if (other || OWN_TRANSFER.test(text)) {
    return { kind: "traspaso_propio", reason: other ? `Traspaso desde la cuenta propia ${accountLabel(other)}` : "El banco lo describe como traspaso", relatedAccountId: other ? other.id : null };
  }
  if (PAY_CARD.test(text) && !ctx.account) return { kind: "pago_tarjeta_propia", reason: "Pago a la tarjeta: no es gasto", relatedAccountId: null };
  if (REFUND.test(text)) return { kind: "devolucion", reason: "El banco lo describe como devolución o bonificación", relatedAccountId: null };
  if (isCardStatement) return { kind: "devolucion", reason: "Abono en la tarjeta que no es pago", relatedAccountId: null };
  return { kind: "ingreso", reason: "Abono a la cuenta: entra dinero, no es gasto", relatedAccountId: null };
}

/** ¿Cuenta como gasto al confirmar? (la devolución también se guarda, restando). */
export const kindCreatesExpense = (kind: MovementKind | undefined | null) => kind === "gasto" || kind === "devolucion";

export const isMovementKind = (v: unknown): v is MovementKind => MOVEMENT_KINDS.includes(v as MovementKind);

/**
 * Gasto que probablemente revierte una devolución: mismo comercio, monto
 * mayor o igual en la misma moneda, anterior a la devolución y a no más de
 * `maxDays`. Gana el de monto idéntico y, luego, el más cercano en fecha.
 */
export function suggestRefundTarget(
  refund: Pick<ExtractedCharge, "date" | "amount" | "currency" | "vendor">,
  candidates: readonly Pick<Expense, "id" | "date" | "amount" | "currency" | "vendor">[],
  { maxDays = 120 }: { maxDays?: number } = {},
): string | null {
  if (!refund.date) return null;
  const cur = (refund.currency || BASE_CURRENCY).toUpperCase();
  // "DEVOLUCION TIENDA X" se compara como "TIENDA X".
  const vendor = normalizeText(refund.vendor).replace(REFUND_WORDS, " ");
  let best: { id: string; exact: boolean; sim: number; gap: number } | null = null;
  for (const e of candidates) {
    if ((e.currency || BASE_CURRENCY) !== cur || !(e.amount > 0)) continue;
    if (e.amount + 0.005 < refund.amount) continue;
    if (e.date > refund.date) continue;
    const gap = daysBetween(e.date, refund.date);
    if (gap > maxDays) continue;
    const sim = vendorSimilarity(vendor, e.vendor);
    if (sim === 0) continue;
    const exact = Math.abs(e.amount - refund.amount) < 0.005;
    const better =
      !best ||
      (exact && !best.exact) ||
      (exact === best.exact && (sim > best.sim || (sim === best.sim && gap < best.gap)));
    if (better) best = { id: e.id, exact, sim, gap };
  }
  return best ? best.id : null;
}

// --- Cuadre de totales ------------------------------------------------------------------
export interface KindTotals {
  count: number;
  /** En la moneda del estado de cuenta. */
  total: number;
}

export interface Reconciliation {
  currency: string;
  /** Total de cargos que declara el banco; `null` si no lo declara (o no se capturó). */
  declared: number | null;
  /** Suma de TODOS los cargos (salidas) leídos del documento. */
  imported: number;
  /** Suma de los abonos (entradas) leídos. */
  credits: number;
  /** `declared - imported`; `null` sin total declarado. */
  difference: number | null;
  status: ReconcileStatus;
  byKind: Record<MovementKind, KindTotals>;
  /** Cargos en otra moneda que el banco no convirtió (no suman). */
  otherCurrency: number;
}

export interface ReconcileInput {
  status?: string;
  currency?: string | null;
  statementTotal?: number | null;
  charges: readonly (Pick<ExtractedCharge, "amount" | "currency" | "isCredit" | "billedAmount"> & { kind?: MovementKind | null })[];
}

/**
 * Cuadre de una importación. «Importado» es lo que salió de la cuenta según
 * lo leído (todos los cargos, sean gasto o traspaso), porque así suma el
 * banco su «total de cargos». Una importación está conciliada cuando ya se
 * confirmó y su total declarado coincide (±`tolerance`); con diferencia si no
 * coincide; por conciliar mientras no se confirme o falte el total declarado.
 */
export function reconcileImport(input: ReconcileInput, { tolerance = 0.01 }: { tolerance?: number } = {}): Reconciliation {
  const currency = (input.currency || BASE_CURRENCY).toUpperCase();
  const byKind = Object.fromEntries(MOVEMENT_KINDS.map((k) => [k, { count: 0, total: 0 }])) as Record<MovementKind, KindTotals>;
  let imported = 0;
  let credits = 0;
  let otherCurrency = 0;
  for (const c of input.charges) {
    const value = c.billedAmount != null && currency === BASE_CURRENCY ? Number(c.billedAmount) : (c.currency || BASE_CURRENCY).toUpperCase() === currency ? Number(c.amount) : null;
    const kind: MovementKind = isMovementKind(c.kind) ? c.kind : c.isCredit ? "devolucion" : "gasto";
    if (value == null || !Number.isFinite(value)) {
      otherCurrency += 1;
      continue;
    }
    byKind[kind].count += 1;
    byKind[kind].total = round2(byKind[kind].total + value);
    if (c.isCredit) credits += value;
    else imported += value;
  }
  imported = round2(imported);
  credits = round2(credits);
  const declared = input.statementTotal == null || !Number.isFinite(Number(input.statementTotal)) ? null : round2(Number(input.statementTotal));
  const difference = declared == null ? null : round2(declared - imported);
  let status: ReconcileStatus = "por_conciliar";
  if (input.status === "confirmada" && difference != null) status = Math.abs(difference) <= tolerance ? "conciliado" : "con_diferencia";
  return { currency, declared, imported, credits, difference, status, byKind, otherCurrency };
}

/** Clasifica todos los cargos de una importación con la cuenta elegida. */
export function classifyCharges<T extends Pick<ExtractedCharge, "vendor" | "description" | "isCredit">>(
  charges: readonly T[],
  ctx: ClassifyContext,
): (T & { kind: MovementKind; kindReason: string | null; relatedAccountId: string | null })[] {
  return charges.map((c) => {
    const k = classifyMovement(c, ctx);
    return { ...c, kind: k.kind, kindReason: k.reason, relatedAccountId: k.relatedAccountId };
  });
}

/** Devoluciones: propone el cargo que revierten (existente o del mismo documento). */
export function linkRefunds<T extends StatementCharge>(
  charges: readonly T[],
  existing: readonly Pick<Expense, "id" | "date" | "amount" | "currency" | "vendor">[],
): T[] {
  const sameDoc = charges
    .filter((c) => !c.isCredit && c.date && (c.kind ?? "gasto") === "gasto")
    .map((c) => ({ id: `#${c.index}`, date: c.date as string, amount: c.amount, currency: c.currency, vendor: c.vendor }));
  return charges.map((c) => {
    if (c.kind !== "devolucion" || c.revertsExpenseId || c.revertsIndex != null) return c;
    const target = suggestRefundTarget(c, [...sameDoc, ...existing]);
    if (!target) return c;
    return target.startsWith("#") ? { ...c, revertsIndex: Number(target.slice(1)) } : { ...c, revertsExpenseId: target };
  });
}
